import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";

import API from "../services/API";
import { setSession } from "../services/auth";
import { landingPageToPath } from "../services/settings";
import { extractErrorMessage, extractStatus } from "../utils/errorMessage";
import { notifySuccess } from "../services/notification.service";

declare global {
  interface Window {
    google?: {
      accounts: {
        id: {
          initialize: (config: {
            client_id: string;
            callback: (response: { credential: string }) => void;
            context?: string;
            ux_mode?: string;
            nonce?: string;
          }) => void;
          renderButton: (
            parent: HTMLElement,
            options: Record<string, unknown>,
          ) => void;
          prompt: (() => void) | undefined;
        };
      };
    };
  }
}

const inputClass =
  "w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm text-slate-900 placeholder:text-slate-400 shadow-sm transition focus:border-[var(--color-primary)] focus:outline-none focus:ring-4 focus:ring-[var(--color-primary)]/15 disabled:cursor-not-allowed disabled:bg-slate-50";

const labelClass = "text-sm font-medium text-slate-700";

const getPostLoginPath = (userId?: number | string) =>
  landingPageToPath(undefined, userId);

function FloatingBackground() {
  return (
    <div
      className="pointer-events-none fixed inset-0 overflow-hidden"
      aria-hidden="true"
    >
      {/* Top-left corner blur — brand teal */}
      <div className="absolute -top-24 -left-24 h-[28rem] w-[28rem] rounded-full bg-emerald-50 blur-3xl" />
      {/* Bottom-right corner blur — brand gradient blue */}
      <div className="absolute -bottom-24 -right-24 h-[28rem] w-[28rem] rounded-full bg-[#0B95B8]/20 blur-3xl" />

      {/* Floating watermark words — visible but subtle */}
      <span className="absolute top-[8%] left-[6%] text-[4.5rem] font-black tracking-tighter text-[var(--color-primary)]/15 select-none">
        TRACK
      </span>
      <span className="absolute top-[14%] right-[8%] text-[3.5rem] font-black tracking-tighter text-[var(--color-primary)]/20 select-none">
        TIME
      </span>
      <span className="absolute top-[35%] left-[3%] text-[3rem] font-black tracking-tighter text-[var(--color-primary)]/15 select-none">
        LOGS
      </span>
      <span className="absolute top-[42%] right-[4%] text-[4rem] font-black tracking-tighter text-[var(--color-primary)]/15 select-none">
        PLAN
      </span>
      <span className="absolute bottom-[18%] left-[10%] text-[3rem] font-black tracking-tighter text-[var(--color-primary)]/15 select-none">
        WORK
      </span>
      <span className="absolute bottom-[10%] right-[12%] text-[3.5rem] font-black tracking-tighter text-[var(--color-primary)]/20 select-none">
        BUILD
      </span>
      <span className="absolute bottom-[30%] right-[2%] text-[2.5rem] font-black tracking-tighter text-[var(--color-primary)]/15 select-none">
        SHIP
      </span>
      <span className="absolute top-[58%] left-[2%] text-[2rem] font-black tracking-tighter text-[var(--color-primary)]/15 select-none">
        FOCUS
      </span>

      {/* Timeline bars */}
      <div className="absolute top-[24%] left-[4%] h-1 w-48 sm:w-64 rounded-full bg-gradient-to-r from-[var(--color-primary)]/20 to-transparent" />
      <div className="absolute top-[32%] right-[6%] h-1 w-40 sm:w-52 rounded-full bg-gradient-to-l from-slate-400/25 to-transparent" />
      <div className="absolute bottom-[28%] left-[8%] h-px w-36 sm:w-44 rounded-full bg-gradient-to-r from-[var(--color-primary)]/15 to-transparent" />
      <div className="absolute bottom-[22%] right-[10%] h-px w-32 sm:w-40 rounded-full bg-gradient-to-l from-slate-400/20 to-transparent" />
      <div className="absolute top-[52%] right-[15%] h-0.5 w-28 rounded-full bg-gradient-to-l from-emerald-500/25 to-transparent" />

      {/* Large circles (clock / pie-chart suggestion) */}
      <div className="absolute top-[12%] right-[20%] h-44 w-44 rounded-full border border-slate-300/35" />
      <div className="absolute top-[14%] right-[22%] h-44 w-44 rounded-full border border-emerald-200" />
      <div className="absolute bottom-[8%] left-[15%] h-36 w-36 rounded-full border border-slate-300/35" />
      <div className="absolute bottom-[10%] left-[17%] h-36 w-36 rounded-full border border-[#0B95B8]/20" />
      <div className="absolute top-[48%] right-[2%] h-24 w-24 rounded-full border border-slate-300/30" />

      {/* Small dots */}
      <div className="absolute top-[20%] left-[45%] h-2 w-2 rounded-full bg-emerald-50" />
      <div className="absolute top-[38%] right-[35%] h-1.5 w-1.5 rounded-full bg-slate-400/45" />
      <div className="absolute bottom-[35%] left-[42%] h-2 w-2 rounded-full bg-[#0B95B8]/45" />
      <div className="absolute top-[55%] left-[30%] h-1 w-1 rounded-full bg-slate-400/50" />
      <div className="absolute bottom-[45%] right-[25%] h-1 w-1 rounded-full bg-emerald-50" />
      <div className="absolute top-[65%] right-[40%] h-1.5 w-1.5 rounded-full bg-slate-400/45" />

      {/* Ghost card shapes */}
      <div className="absolute top-[28%] right-[18%] h-14 w-20 rounded-xl border border-slate-300/30" />
      <div className="absolute top-[30%] right-[20%] h-14 w-20 rounded-xl border border-emerald-200" />
      <div className="absolute bottom-[36%] left-[22%] h-12 w-16 rounded-lg border border-slate-300/30" />

      {/* Thin vertical accent lines */}
      <div className="absolute top-[18%] left-[48%] h-24 w-px bg-gradient-to-b from-slate-400/30 to-transparent" />
      <div className="absolute bottom-[20%] right-[45%] h-20 w-px bg-gradient-to-t from-emerald-500/30 to-transparent" />
    </div>
  );
}

export default function Login() {
  const navigate = useNavigate();
  const googleInitialized = useRef(false);

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showPasswordForm, setShowPasswordForm] = useState(false);
  const [magicLinkSending, setMagicLinkSending] = useState(false);
  const [magicLinkSent, setMagicLinkSent] = useState(false);

  const endpoint = "/api/auth/login";

  const handleGoogleResponse = async (response: { credential: string }) => {
    setSubmitting(true);
    setError(null);
    try {
      const res = await API.post("/api/auth/google", {
        idToken: response.credential,
      });

      const payload = res.data?.data;
      const token: string | undefined = payload?.accessToken ?? payload?.token;
      const refreshToken: string | undefined = payload?.refreshToken;
      const role: string | undefined = payload?.user?.role;

      if (!token || !role) {
        setError("Google login succeeded but the response was malformed.");
        return;
      }

      setSession({ token, refreshToken: refreshToken ?? null });

      navigate(getPostLoginPath(payload?.user?.id), { replace: true });
    } catch (err: unknown) {
      console.error("Google login error: ", err);
      const status = extractStatus(err);
      const apiMessage = extractErrorMessage(err, "");
      if (status === 401) {
        setError(apiMessage || "Contact admin: user not found");
      } else if (status === 409) {
        navigate(
          `/already-logged-in?email=${encodeURIComponent(email.trim())}`,
          {
            replace: true,
          },
        );
        return;
      } else if (status === 500) {
        setError("Server error. Please try again in a moment.");
      } else {
        setError(
          apiMessage || "Could not log in with Google. Please try again.",
        );
      }
    } finally {
      setSubmitting(false);
    }
  };

  // Google Sign In initialization — guard against double initialization
  // (React StrictMode mounts/unmounts components twice in dev).
  // The GSI script is loaded async/defer, so it may not be available on
  // first render. We poll until it arrives (up to ~5s).
  useEffect(() => {
    const clientId = import.meta.env.VITE_GOOGLE_CLIENT_ID as
      | string
      | undefined;
    if (!clientId) return;

    let cancelled = false;

    const initGoogle = () => {
      if (cancelled || !window.google) return;

      if (!googleInitialized.current) {
        window.google.accounts.id.initialize({
          client_id: clientId,
          callback: handleGoogleResponse,
          context: "signin",
          ux_mode: "popup",
        });
        googleInitialized.current = true;
      }
    };

    if (window.google) {
      initGoogle();
    } else {
      let attempts = 0;
      const maxAttempts = 25;
      const interval = setInterval(() => {
        attempts++;
        if (window.google) {
          clearInterval(interval);
          initGoogle();
        } else if (attempts >= maxAttempts) {
          clearInterval(interval);
        }
      }, 200);

      return () => {
        cancelled = true;
        clearInterval(interval);
      };
    }

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSendMagicLink = async () => {
    setMagicLinkSent(false);
    setError(null);

    if (!email.trim()) {
      setError("Enter your email to receive a magic link.");
      return;
    }

    setMagicLinkSending(true);
    try {
      await API.post("/api/auth/magic-link", { email: email.trim() });
      setMagicLinkSent(true);
    } catch (err: unknown) {
      console.error("Magic link error: ", err);
      const apiMessage = extractErrorMessage(err, "");
      setError(apiMessage || "Could not send magic link. Please try again.");
    } finally {
      setMagicLinkSending(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!email.trim() || !password) {
      setError("Email and password are required.");
      return;
    }

    setSubmitting(true);

    try {
      const res = await API.post(endpoint, {
        email: email.trim(),
        password,
      });

      const payload = res.data?.data;
      const token = payload?.accessToken ?? payload?.token;
      const refreshToken = payload?.refreshToken;
      const role = payload?.user?.role;

      if (!token || !role) {
        setError("Login succeeded but response was invalid.");
        return;
      }

      setSession({ token, refreshToken: refreshToken ?? null });

      notifySuccess(
        "Login Successful",
        `Welcome back${payload?.user?.first_name ? "," + payload.user.first_name : ""}!`,
      );
      navigate(getPostLoginPath(payload?.user?.id), { replace: true });
    } catch (err: unknown) {
      const status = extractStatus(err);
      const apiMessage = extractErrorMessage(err, "");

      if (status === 401) setError(apiMessage || "Invalid credentials.");
      else if (status === 409) {
        navigate(
          `/already-logged-in?email=${encodeURIComponent(email.trim())}`,
          {
            replace: true,
          },
        );
        return;
      } else if (status === 500) setError("Server error. Try again later.");
      else setError(apiMessage || "Login failed. Try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="relative min-h-screen w-full bg-slate-50 text-slate-900">
      {/* Background decorations — fixed, behind everything */}
      <FloatingBackground />

      {/* Centered form */}
      <div className="relative z-10 mx-auto flex min-h-screen max-w-md items-center justify-center px-4 py-8">
        <div className="w-full rounded-2xl border border-slate-200 bg-white p-6 shadow-xl sm:p-8">
          <div className="mb-5">
            <p className="text-xs font-semibold uppercase tracking-wider bg-gradient-brand-text">
              Welcome back
            </p>
            <h2 className="mt-1 text-2xl font-bold text-slate-900">Sign in</h2>
            <p className="mt-1 text-sm text-slate-500">
              Enter your email and choose a sign-in method
            </p>
          </div>

          {error && (
            <div className="mb-4 text-sm text-red-600 bg-red-50 border border-red-200 px-3 py-2 rounded-lg">
              {error}
            </div>
          )}

          {/* EMAIL — always visible */}
          <div className="mb-3">
            <label htmlFor="email" className={labelClass}>
              Email
            </label>
            <input
              id="email"
              type="email"
              className={inputClass + "mt-1"}
              placeholder="you@company.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </div>

          {/* PASSWORD SECTION — revealed on click */}
          {showPasswordForm && (
            <>
              <div className="mb-3">
                <label htmlFor="password" className={labelClass}>
                  Password
                </label>
                <div className="relative mt-1">
                  <input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    className={inputClass + "pr-16"}
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((v) => !v)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-slate-500 hover:text-role-primary"
                  >
                    {showPassword ? "Hide" : "Show"}
                  </button>
                </div>
              </div>

              <div className="flex justify-end mb-3">
                <button
                  type="button"
                  onClick={() => navigate("/forgot-password")}
                  className="text-xs text-role-primary hover:text-[#009E93] font-medium transition"
                >
                  Forgot password?
                </button>
              </div>

              <button
                type="button"
                onClick={handleSubmit}
                disabled={submitting}
                className="w-full rounded-xl bg-gradient-brand text-white font-medium py-2.5 transition disabled:opacity-60 mb-3 shadow-sm hover:shadow-md"
              >
                {submitting ? "Signing in..." : "Sign in"}
              </button>
            </>
          )}

          {/* ACTION BUTTONS */}
          {!showPasswordForm && (
            <button
              type="button"
              onClick={() => setShowPasswordForm(true)}
              className="w-full rounded-xl bg-gradient-brand text-white font-medium py-2.5 transition mb-2.5 shadow-sm hover:shadow-md"
            >
              Login with password
            </button>
          )}

          <button
            type="button"
            onClick={handleSendMagicLink}
            disabled={magicLinkSending}
            className="w-full rounded-xl border border-slate-200 bg-white text-slate-700 font-medium py-2.5 transition hover:bg-slate-50 disabled:opacity-60 mb-2.5"
          >
            {magicLinkSending
              ? "Sending..."
              : magicLinkSent
                ? "Magic link sent — check your email"
                : "Send magic link for login"}
          </button>

          {/* GOOGLE SIGN IN */}
          <button
            type="button"
            disabled={submitting}
            onClick={() => {
              const clientId = import.meta.env.VITE_GOOGLE_CLIENT_ID as
                | string
                | undefined;
              if (!clientId) {
                setError("Google Sign-In is not configured. Contact admin.");
                return;
              }
              if (window.google) {
                if (!googleInitialized.current) {
                  window.google.accounts.id.initialize({
                    client_id: clientId,
                    callback: handleGoogleResponse,
                    context: "signin",
                    ux_mode: "popup",
                  });
                  googleInitialized.current = true;
                }
                window.google.accounts.id.prompt?.();
              } else {
                setError(
                  "Google Sign-In is still loading. Please try again in a moment.",
                );
              }
            }}
            className="w-full flex items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white text-slate-700 font-medium py-2.5 transition hover:bg-slate-50 disabled:opacity-60 mb-2.5"
          >
            <svg
              className="h-5 w-5 shrink-0"
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              <path
                d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                fill="#4285F4"
              />
              <path
                d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                fill="#34A853"
              />
              <path
                d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                fill="#FBBC05"
              />
              <path
                d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                fill="#EA4335"
              />
            </svg>
            Login with Google
          </button>

          <p className="text-center text-xs text-slate-400 mt-3">
            Contact admin if you don&apos;t have access
          </p>
        </div>
      </div>
    </div>
  );
}
